import { useMemo } from "react";
import type { ITableCell, ITableSpecificSettings } from "../types";
import { useTableCellBorders } from "./use-table-cell-borders";

export type ITableCellStyleSettings = Pick<
  ITableSpecificSettings,
  | "tableHasAlternatingRows"
  | "tableCellBgColor"
  | "tableHeaderBgColor"
  | "tableAlternatingRowsBgColor"
  | "tableCellTextColor"
  | "tableHeaderTextColor"
  | "tableAlternatingRowsTextColor"
  | "tableCellTextAlignment"
  | "tableCellTextOrientation"
  | "tableCellFontSize"
  | "tableHasHeader"
  | "tableCols"
  | "tableRows"
  | "tableCellBorderColor"
  | "tableCellBorderWidth"
  | "tableCellBorderStyle"
  | "tableAlternatingRowsBorderColor"
  | "tableHeaderPosition"
  | "tableHeaderBorderColor"
  | "round"
>;

interface IProps extends ITableCellStyleSettings {
  isHeader: boolean;
  cell: ITableCell;
  row: number;
  col: number;
  // True when the neighbour above/left actually draws the shared edge.
  topNeighborDrawsBottom: boolean;
  leftNeighborDrawsRight: boolean;
}

export const useTableCellStyles = ({
  isHeader,
  cell,
  row,
  col,
  topNeighborDrawsBottom,
  leftNeighborDrawsRight,
  tableHasAlternatingRows,
  tableCellBgColor,
  tableHeaderBgColor,
  tableAlternatingRowsBgColor,
  tableCellTextColor,
  tableHeaderTextColor,
  tableAlternatingRowsTextColor,
  tableCellTextAlignment,
  tableCellTextOrientation,
  tableCellFontSize,
  tableHasHeader,
  tableCols,
  tableRows,
  tableCellBorderColor,
  tableCellBorderWidth,
  tableCellBorderStyle,
  tableAlternatingRowsBorderColor,
  tableHeaderPosition,
  tableHeaderBorderColor,
  round,
}: IProps) => {
  const tdStyles = useMemo(() => {
    const isAlt = !isHeader && tableHasAlternatingRows && row % 2 !== 0;

    let background = tableCellBgColor;
    if (cell.bgColor) background = cell.bgColor;
    else {
      if (isHeader) background = tableHeaderBgColor;
      else if (isAlt) background = tableAlternatingRowsBgColor;
    }

    let textColorVar = tableCellTextColor;
    if (isHeader) textColorVar = tableHeaderTextColor;
    else if (isAlt) textColorVar = tableAlternatingRowsTextColor;

    let fontSizeVar: string | undefined;
    if (!cell.hasFroalaFontSize) {
      fontSizeVar = `${tableCellFontSize}px`;
    }

    return {
      background: background ?? tableCellBgColor,
      "--table-text-color": textColorVar ?? tableCellTextColor,
      "--table-text-alignment": tableCellTextAlignment,
      "--table-text-orientation": tableCellTextOrientation,
      "--table-font-size": fontSizeVar,
    };
  }, [
    cell,
    row,
    isHeader,
    tableHasAlternatingRows,
    tableCellBgColor,
    tableHeaderBgColor,
    tableAlternatingRowsBgColor,
    tableCellTextColor,
    tableHeaderTextColor,
    tableAlternatingRowsTextColor,
    tableCellTextAlignment,
    tableCellTextOrientation,
    tableCellFontSize,
  ]);

  const borderStyles = useTableCellBorders({
    isHeader,
    cell,
    col,
    row,
    tableHasHeader,
    tableCols,
    tableRows,
    tableCellBorderColor,
    tableCellBorderWidth,
    tableCellBorderStyle,
    tableHasAlternatingRows,
    tableAlternatingRowsBorderColor,
    tableHeaderPosition,
    tableHeaderBorderColor,
    round,
    topNeighborDrawsBottom,
    leftNeighborDrawsRight,
  });

  const contentRotationStyle: Record<string, string> = {};
  if (cell.textRotation === "down") {
    contentRotationStyle.writingMode = "vertical-rl";
  } else if (cell.textRotation === "up") {
    contentRotationStyle.writingMode = "vertical-rl";
    contentRotationStyle.transform = "rotate(180deg)";
  }

  return {
    ...borderStyles,
    bgStyles: {
      borderRadius: "inherit",
      width: "100%",
      height: "100%",
      background: tdStyles.background,
    },
    tdStyles: {
      ...borderStyles.tdStyles,
      ...tdStyles,
      // Prevents cell bg from painting under its own borders, which made
      // borders look thinner when adjacent cells had backgrounds.
      backgroundClip: "padding-box",
    },
    contentRotationStyle,
  };
};
